/**
 * Who the uncapped money went to.
 *
 * This replaced a table of a thousand cheques, twenty-five to a page. The
 * table was accurate and unreadable: the recipient is free text tied to no
 * identifier, so the one fact worth taking away — that 71% of every
 * uncapped cheque disclosed on these forms names one man — was spread
 * across fifty-eight spellings of his name and forty pages.
 *
 * Grouping those spellings is the only inference on the site, so the chart
 * carries it in the open: the rule is named under the bars, the money that
 * names no president is a bar of its own rather than a rounding, and the
 * cheques as filed are one click away underneath.
 *
 * Horizontal, because these are names of varying length and a vertical axis
 * of rotated labels is a chart you read with your head tilted. Plain divs
 * rather than SVG for the same reason: the bars are one dimension.
 */

import { OTHER_KINDS, type OtherKind, type OtherMoney } from "../api";
import { brief } from "./party";

const TINTS: Record<OtherKind, string> = {
  ple: "bg-brand",
  pic: "bg-brand/55",
  me: "bg-brand/25",
};

const ORDER: OtherKind[] = ["ple", "pic", "me"];

export function WhereItWent({ data }: { data: OtherMoney }) {
  const bars = data.by_recipient;
  if (bars.length === 0) return null;
  const peak = Math.max(...bars.map((b) => b.total), 1);
  // The authoritative total rather than the sum of the bars drawn. They
  // are equal today, and a share that quietly renormalises to whatever is
  // on screen is how a chart starts overstating itself later.
  const all = data.around_them || bars.reduce((sum, b) => sum + b.total, 0) || 1;

  return (
    <figure className="mt-8">
      <figcaption className="mb-1">
        <h3 className="font-display text-lg font-semibold">Who received it</h3>
        <p className="mt-1 max-w-3xl text-sm text-muted">
          Every uncapped cheque on these forms, against the president it names. Hover a bar for
          what the money was for.
        </p>
      </figcaption>
      <div className="mb-4 mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-muted">
        {ORDER.map((kind) => (
          <span key={kind} className="inline-flex items-center gap-2">
            <span className={`inline-block h-3 w-3 rounded-sm ${TINTS[kind]}`} />
            {OTHER_KINDS[kind].label}
          </span>
        ))}
      </div>
      <ul className="space-y-2.5">
        {bars.map((row) => (
          <li key={row.recipient ?? "residual"}>
            <div className="flex items-baseline justify-between gap-4 text-sm">
              <span className={row.recipient ? "font-medium" : "text-muted"}>
                {/* Never given a person's name. It is a leftover, and
                    "Unknown" or "Other" would read as one anonymous donor
                    the size of a president. */}
                {row.recipient ?? "Named no president"}
              </span>
              <span className="shrink-0 tabular-nums text-muted">
                {brief(row.total)}
                <span className="ml-2 text-faint">
                  {Math.round((row.total / all) * 100)}%
                </span>
              </span>
            </div>
            <div className="mt-1 flex h-3 overflow-hidden rounded-full bg-sunk">
              {/* One bar, split by what the money was for, so the stack
                  matches the chart above and the legend serves both. */}
              {ORDER.map((kind) => {
                const part = row.parts.find((p) => p.kind === kind)?.total ?? 0;
                if (part <= 0) return null;
                return (
                  <div
                    key={kind}
                    className={TINTS[kind]}
                    // A floor, because the smallest real cheques here are
                    // $75 and $500 against a $116m peak -- a fraction of a
                    // pixel, and an invisible bar on a row that names an
                    // amount reads as a fault rather than as a small
                    // number. Only ever applied to money that exists; a
                    // kind with nothing draws nothing.
                    style={{ width: `${(part / peak) * 100}%`, minWidth: "3px" }}
                    title={`${OTHER_KINDS[kind].label}: ${brief(part)}`}
                  />
                );
              })}
            </div>
          </li>
        ))}
      </ul>
      <figcaption className="mt-4 max-w-3xl text-sm leading-relaxed text-muted">
        The recipient is free text on this form and is tied to no identifier — fifty-eight
        different strings here mention Trump. They are grouped by the president each one names,
        which is the only place on this site where anything is inferred from a filing rather than
        read off it. Where a form is ambiguous, “President Bush” naming one of two men, it is
        counted in neither. Every cheque below keeps the wording exactly as it was filed.
      </figcaption>
    </figure>
  );
}
